import {Keypoints, PointName, predictedHeight} from '@kemtai/keypoints';
import {SideT, TugState} from './types';

const body:  PointName[] = ['left_shoulder', 'right_shoulder', 'left_hip', 'right_hip'] 
const front: PointName[] = ['front_1', 'front_2', 'front_3', 'front_4', 'front_5', 'neck'] 
const back:  PointName[] = ['back_1', 'back_2', 'back_3', 'back_4', 'back_5', 'neck_back']

function degrees(radians: number): number {
    return radians * 180 / Math.PI;
}

function abs(x: number|null):  number|null {
    if (x === null) {
        return null
    }
    return Math.abs(x)
}

function all_present(kp: Keypoints, points: PointName[]): boolean {
    return points.every(k => kp[k])
}
function all_absent(kp: Keypoints, points: PointName[]): boolean {
    return !points.some(k => kp[k])
}

function is_frontal_view(kp: Keypoints):  boolean {
  return all_present(kp, body) && all_present(kp, front) &&  all_absent(kp, back);
}

function is_back_view(kp: Keypoints):  boolean {
  return all_present(kp, body) && all_present(kp, back) && all_absent(kp, front);
}

function is_profile_view(kp: Keypoints):  boolean {
    if (!all_present(kp, body)) {
        return false
    }
    if (!all_present(kp, back)) {
        return false
    }
    if (!all_present(kp, front)) {
        return false
    }

    const h = predictedHeight(kp)
    if (h === null) {
        return false
    }
    const d = kp.dist('left_shoulder', 'right_shoulder')
    if (d === null) {
        return false
    }

    if (d / h * 175 > 30) {
        return false
    }
    return true
}

function get_view_type(kp: Keypoints): string|null {
    if (is_frontal_view(kp)) {
        return 'Frontal'
    }
    if (is_back_view(kp)) {
        return 'Back'
    }
    if (is_profile_view(kp)) {
        return 'Profile'
    }
    return null
} 

function meanX(kp: Keypoints, points: PointName[]): number|null {
    let sum = 0
    let count = 0
    for (let p of points) {
        if (kp[p]) {
            sum += kp[p]!.x
            count += 1
        }
    }
    if (count === 0) {
        return null
    }
    return sum / count
}

function profileSide(kp: Keypoints):  SideT|null {
    const frontX = meanX(kp, front)
    const backX = meanX(kp, back)
    if (frontX === null || backX === null) {
        return null
    }
    if (frontX < backX) {
        return 'left'
    } else {
        return 'right'
    }
}



function hAngle(kp: Keypoints, point1: PointName, point2: PointName): number|null {
    const p1 = kp[point1] 
    const p2 = kp[point2] 
    if (!p1 || !p2) {
        return null
    }
    return degrees(Math.atan2(p1.y - p2.y, p1.x - p2.x))
}

type StateT = "waiting"|"measuring"|null|"done";

export class SitToStandLogic {
    viewType: string|null = null;
    userPosition: string|null = null;
    state:    StateT = null;
    standUpTime: number|null = null;
    duration: number|null = null;   
    result: number|null = null;
    popupMessage: string|null = null;


    reset() {
        this.viewType = null;
        this.state = null;
        this.standUpTime = null;
        this.duration = null;   
        this.result = null;
        this.popupMessage = null;
    }

    tugState(): TugState {
        return {
            viewType: this.viewType, 
            userPosition: this.userPosition,
            measuring : this.state === 'measuring',
            duration: this.duration,
            result: this.result,
            state : this.state,
            popupMessage: this.popupMessage
        }
    }

    processFrame(kp: Keypoints): TugState {

        this.viewType = get_view_type(kp);
        this.userPosition = null;
        let standing = false
        let sitting = false
        if (this.viewType === 'Profile') {
            const _profileSide = profileSide(kp);
            if (_profileSide) {
                let angle = abs(hAngle(kp, `${_profileSide}_hip`, `${_profileSide}_knee`))
                if (angle !== null) {
                    if (angle < 0) {
                        angle = -angle
                    }
                    if (angle > 90) {
                        angle = 180 - angle
                    }
                    if (angle > 45) {
                        standing = true
                        this.userPosition = 'Standing'
                    } else {
                        sitting = true
                        this.userPosition = 'Sitting'
                    }
                }
            }
        }



        if (this.result !== null) {
            return this.tugState();
        }

        if (this.userPosition === null) {
            if (this.state === 'waiting') {
                this.state = null;
            }

        } else if (this.userPosition === 'Standing') {
            if (this.state === 'waiting') {
                // user just stood up, start measuring
                this.state = 'measuring';
                this.result = null;
                this.standUpTime = Date.now();
            }
        } else if (this.userPosition === 'Sitting') {
            if (this.state === 'measuring') {
                // user just sat down, stop measuring
                this.result = Date.now() - this.standUpTime!;
                if (this.result > 1000) {
                    this.state = 'done';
                } else {
                    this.state = 'waiting';
                    this.result = null;
                }
            } else {
                this.state = 'waiting';
            }
        }

        if (this.state === 'measuring') {
            // update duration
            this.duration = Date.now() - this.standUpTime!;
        }

        return  this.tugState();
    }
}
